import { createContext, useContext, useEffect, useState } from "react";
import { ResidentModel } from "app/services/api/types/residents/models/ResidentModel";
import { useGetResidentQuery } from "app/services/api/residentsApi";
import { useSelector } from "react-redux";
import { RootState } from "app/services/store";
import { selectResident } from "app/store/slices/residents";
import { useRouter } from "expo-router";
import { ResidentsPath } from "app/constants/routePaths";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
interface ResidentProviderProps {
  children: React.ReactNode;
  initialResidentId?: number;
  residentModel?: ResidentModel;
}

interface ResidentContextValue {
  residentId?: number;
  resident?: ResidentModel;
  loading: boolean;
  setResidentDetails: (resident: ResidentModel) => void;
}

const ResidentContext = createContext<ResidentContextValue>(
  {} as ResidentContextValue,
);

export function ResidentProvider({
  children,
  initialResidentId,
  residentModel,
}: ResidentProviderProps) {
  const router = useRouter();
  const [residentId, setResidentId] = useState<number | undefined>(
    residentModel?.id ?? initialResidentId ?? undefined,
  );
  const { showErrorToast } = useDisplayToast();

  const residentFromStore = useSelector((state: RootState) =>
    selectResident(state, residentId!),
  );
  const { data, isLoading, isError, error } = useGetResidentQuery(
    { resident: residentId! },
    { skip: !residentId },
  );

  useEffect(() => {
    if (isError) {
      showErrorToast(
        "Something went wrong and we were not able to find resident with id " +
          residentId,
        { title: "Resident not found" },
      );
      router.replace(ResidentsPath);
    }
  }, [isError]);

  const resident = data ?? residentModel ?? residentFromStore;

  const value: ResidentContextValue = {
    residentId,
    resident,
    loading: isLoading,
    setResidentDetails: (resident: ResidentModel) => {
      setResidentId(resident.id);
    },
  };

  return (
    <ResidentContext.Provider value={value}>
      {children}
    </ResidentContext.Provider>
  );
}

export function useResidentContext() {
  const context = useContext(ResidentContext);
  if (!context) {
    throw new Error(
      "ResidentProviderContext must be used within a ResidentProvider",
    );
  }
  return context;
}
